import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import React, { useState, useEffect, Fragment } from 'react';

import { Grid, Typography } from '@mui/material';

import PaymentFilters from './PaymentFilters';
import PaymentsSummary from './PaymentsSummary';
import { snackAlertData } from 'utils/constants';
import PaymentActionModal from './PaymentActionModal';
import { initialValues } from '../../utils/paymentSchema';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { useCompanyService } from 'store/services/company.service';
import { usePaymentsService } from 'store/services/payments.service';
import { paymentTableHeaders } from '../../utils/paymentsTableHeaders';
import { DataTableWithAccordion, CircleLoading, TablePagination, SnackBarAlert } from 'shared';



const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;

const PaymentsTab = () => {
    const { getPayments, getPaymentsSummary } = usePaymentsService();
    const { getCompanyIntegrations } = useCompanyService();
    const companyId = useSelector((state) => state?.company?.data?.id);

    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({});
    const [sortOrder, setSortOrder] = useState('desc');
    const [isInvoices, setIsInvoices] = useState(null);
    const [isPayments, setIsPayments] = useState(null);
    const [tableLoading, setTableLoading] = useState(true);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [paymentSummary, setPaymentsSummary] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [sorting, setSorting] = useState({ payment_date: true });
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [filterResultLoader, setFilterResultLoader] = useState(false);
    const [integrationStatuses, setIntegrationStatuses] = useState({});



    const fetchData = async (pageNumber = 1, filters = {}, column = null, sortOrder = null) => {
        try {
            setTableLoading(true);
            const response = await getPayments({
                companyId: companyId,
                page: pageNumber,
                sortColumn: column,
                sortOrder,
                ...filters,
            });
            setData(response);
        } catch (error) {
            handleErrorAlert(setAlertData);

        } finally {
            setTableLoading(false);
        }
    };

    const setIntegrationsLoading = async () => {
        try {
            const response = await getCompanyIntegrations(companyId);
            const statuses = response?.data?.integrations_status_data || {};
            setIntegrationStatuses(statuses);

            if (statuses.skybox_status) {
                setIsInvoices(true);
            }

            if (
                statuses.automatiq_b2b_status ||
                statuses.seat_geek_status ||
                statuses.vivid_seats_status
            ) {
                setIsPayments(true);
            }
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleGetPaymentsSummary = async () => {
        try {
            const response = await getPaymentsSummary(companyId);
            setPaymentsSummary(response.data);
        } catch (error) {
            handleErrorAlert(setAlertData);

        }
    };

    const handlePageChange = async (pageNumber) => {
        setPage(pageNumber);
        await fetchData(pageNumber, filters, Object.keys(sorting)[0], sortOrder);
    };

    const sortHandler = async (columnId, newSortOrder) => {
        setSorting({ [columnId]: true });
        setSortOrder(newSortOrder);
        fetchData(page, filters, columnId, newSortOrder);
    };

    const handleFilterResultsClick = async () => {
        try {
            setFilterResultLoader(true);
            const newFilters = {
                ...initialValues,
                status: 'not found,pending',
                type: initialValues.type.value,
                marketplace: initialValues.marketplace.value,
            };
            setFilters(newFilters);
            await fetchData(page, newFilters, Object.keys(sorting)[0], sortOrder);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setFilterResultLoader(false);
        }
    };

    const handleMatchClick = (data) => {
        setSelectedPayment(data);
        setOpenPaymentModal(true);
    };

    const handlePaymentModalSuccess = (message) => {
        try {
            setOpenPaymentModal(false);
            handleSuccessAlert(setAlertData, message);
            handleGetPaymentsSummary();
            fetchData(page, filters, Object.keys(sorting)[0], sortOrder);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleFiltersSubmit = async (values) => {
        setFilters(values);
        fetchData(1, values, Object.keys(sorting)[0], sortOrder);
    };

    useEffect(() => {
        if (companyId) {
            fetchData(page, filters, Object.keys(sorting)[0], sortOrder);
            handleGetPaymentsSummary();
            setIntegrationsLoading();

        }
    }, [companyId, page]);


    useEffect(() => {
        if (!companyId) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `integration-status/${companyId}/`;

        // automatiq_b2b_status
        // vivid_seats_status"
        // skybox_status
        // seat_geek_status
        socket.off(eventName);
        socket.on(eventName, (data) => {
            setIntegrationStatuses((prevStatuses) => {
                const newStatuses = { ...prevStatuses, ...data };

                if (
                    !newStatuses.automatiq_b2b_status &&
                    !newStatuses.vivid_seats_status &&
                    !newStatuses.seat_geek_status
                ) {
                    setIsPayments(false);
                    if (prevStatuses.automatiq_b2b_status || prevStatuses.vivid_seats_status || prevStatuses.seat_geek_status) {
                        handleSuccessAlert(setAlertData, 'Payments successfully fetched');
                        fetchData();
                    }
                }

                if (!newStatuses.skybox_status) {
                    setIsInvoices(false);
                    if (prevStatuses.skybox_status) {
                        handleSuccessAlert(setAlertData, 'Invoices successfully fetched');
                        fetchData();
                    }
                }

                return newStatuses;
            });
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyId]);

    return (
        <Fragment>
            {!data || !paymentSummary ? (
                <CircleLoading />
            ) : (
                <Grid container>
                    <Grid item xs={12}>
                        <PaymentsSummary
                            data={paymentSummary}
                            buttonLoader={filterResultLoader}
                            handleFilterResultsClick={handleFilterResultsClick}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <PaymentFilters handleFiltersSubmit={handleFiltersSubmit} isFetchingInvoices={isInvoices} isFetchingPayments={isPayments} />
                    </Grid>
                    {data?.count > 0 && (
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            marginBottom="5px"
                            justifyContent="flex-end"
                        >
                            <Typography fontSize="18px">{`TOTAL: ${data?.count} ROW${data?.count > 1 ? 'S' : ''
                                }`}</Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <DataTableWithAccordion
                            data={data}
                            sorting={sorting}
                            sortOrder={sortOrder}
                            isLoading={tableLoading}
                            sortHandler={(columnId, newSortOrder) =>
                                sortHandler(columnId, newSortOrder)
                            }
                            headers={paymentTableHeaders(handleMatchClick)}
                        />
                    </Grid>
                    <Grid item xs={12} p={'10px 0'}>
                        <TablePagination
                            page={page}
                            count={data?.count || 0}
                            onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                        />
                    </Grid>

                    <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />

                    {openPaymentModal && (
                        <PaymentActionModal
                            data={selectedPayment}
                            setAlertData={setAlertData}
                            openModal={openPaymentModal}
                            setOpenModal={setOpenPaymentModal}
                            handlePaymentModalSuccess={handlePaymentModalSuccess}
                        />
                    )}
                </Grid>
            )}
        </Fragment>
    );
};

export default PaymentsTab;
